"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useRef } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { clearAuthCookies } from "@/lib/api/auth-session"
import { PREFILL_EMAIL_QUERY_PARAM, newFrontendURL } from "@/lib/new-url"
import { signInSchema } from "@/lib/schemas/signIn"
import { cn, t } from "@/lib/utils"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

import {
  AuthIntent,
  redirectIfCookies,
  requestLoginCode,
} from "@/actions/signIn"
import { useBotTrap } from "@/hooks/use-bot-trap"
import { Config, Translation } from "@/types/nango"

const translation: Translation = {
  "forgot-password": { EN: "Forgot password?", FR: "Mot de passe oublié?" },
  login: { EN: "Continue", FR: "Continuer" },
  signup: { EN: "Create account", FR: "Créer un compte" },
  email: { EN: "Enter your email", FR: "Renseignez votre mail" },
  password: { EN: "Enter your password", FR: "Renseignez votre mot de passe" },
  codeSent: { EN: "Code sent successfully", FR: "Code envoyé avec succès" },
  "err-no-account": {
    EN: "No account for this email. Sign up first.",
    FR: "Aucun compte pour cet email. Créez-en un d'abord.",
  },
  "err-account-exists": {
    EN: "An account already exists for this email. Log in instead.",
    FR: "Un compte existe déjà pour cet email. Connectez-vous.",
  },
}

export default function SignInForm({
  config,
  className,
  redirectTo = "/",
  mode,
}: {
  config: Config
  className?: string
  redirectTo?: string
  mode: AuthIntent
}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryEmail = searchParams.get(PREFILL_EMAIL_QUERY_PARAM) || ""
  const { honeypotProps, getBotTrapFields } = useBotTrap()

  const form = useForm<z.infer<typeof signInSchema>>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: queryEmail,
    },
  })

  // Address the form put in the field on its own, whatever the source.
  const prefilledEmail = useRef<string>(queryEmail)

  // Only an address the user was sent with (an invite or a resend link) is
  // filled in. The last-used address is deliberately NOT restored from
  // localStorage: a shared or public machine would hand the previous visitor's
  // email to the next one.
  useEffect(() => {
    if (!queryEmail) return
    prefilledEmail.current = queryEmail
    form.reset({ email: queryEmail })
  }, [queryEmail, form])

  const onSubmit = async (values: z.infer<typeof signInSchema>) => {
    // Cookies already in the jar may still grant a session (possibly after a
    // token refresh): go straight in. Otherwise they are stale, so wipe them
    // before starting a new login.
    if (await redirectIfCookies(config, true, redirectTo)) return
    await clearAuthCookies()

    // Untouched prefilled address: the user only clicked, so the backend must
    // skip its minimum fill time. Editing the field opts back into the check.
    const isEmailPrefilled =
      prefilledEmail.current !== "" && values.email === prefilledEmail.current

    const res = await requestLoginCode(
      values.email,
      config.LOCALE,
      mode,
      config.APP_ID,
      getBotTrapFields(isEmailPrefilled),
    )
    if (res.data !== undefined) {
      toast.success(t(translation, config, "codeSent"))
      router.push(
        newFrontendURL(
          config,
          `${redirectTo}&email=${encodeURIComponent(values.email)}`,
          false,
        ),
      )
      return
    }
    if (res.message === "no_account") {
      toast.error(t(translation, config, "err-no-account"))
      router.push(
        `/?mode=signup&${PREFILL_EMAIL_QUERY_PARAM}=${encodeURIComponent(values.email)}`,
      )
      return
    }
    if (res.message === "account_exists") {
      toast.error(t(translation, config, "err-account-exists"))
      router.push(
        `/?${PREFILL_EMAIL_QUERY_PARAM}=${encodeURIComponent(values.email)}`,
      )
      return
    }
    toast.error(res.message)
  }

  return (
    <Form {...form}>
      <form
        className={cn("w-full space-y-4 py-2", className)}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder={t(translation, config, "email")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* Bot trap: hidden from users, so anything typed here comes from a script. */}
        <div aria-hidden="true" className="sr-only">
          <label htmlFor={honeypotProps.id}>Company website</label>
          <Input {...honeypotProps} />
        </div>
        <div className="mt-6 flex flex-col items-end">
          <Button
            className="w-full"
            type="submit"
            disabled={form.formState.isSubmitting}
          >
            {t(translation, config, mode === "signup" ? "signup" : "login")}
          </Button>
        </div>
      </form>
    </Form>
  )
}
